'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronLeft, ChevronRight, RefreshCw, Users } from 'lucide-react';
import Agent3DCard from '@/app/components/ui/agent-3d-card';
import { useLanguage } from '@/app/context/language-context';
import { useRouter } from 'next/navigation';
import { authService } from '@/services/auth.service';

export default function Enhanced3DAgentsSection() {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const [autoPlay, setAutoPlay] = useState(true);
  const intervalRef = useRef(null);
  const router = useRouter();

  // Filter states
  const [selectedSpecialization, setSelectedSpecialization] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [isSpecializationOpen, setIsSpecializationOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const [agents, setAgents] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const fetchAgents = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await authService.getAgents();

      if (response?.data?.items?.length) {
        // API agents already have is_super_agent and is_trubroker boolean flags
        const apiAgents = response.data.items;

        const filteredReraAgents = apiAgents.filter(
          (agent) => agent.is_rera === true
        );

        setAgents(filteredReraAgents);
        setFilteredAgents(filteredReraAgents);

        const uniqueSpecializations = [
          ...new Set(apiAgents.map((a) => a.specialization).filter(Boolean)),
        ];
        const uniqueLanguages = [
          ...new Set(apiAgents.flatMap((a) => a.languages || [])),
        ];

        setSpecializations(uniqueSpecializations);
        setLanguages(uniqueLanguages);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Apply filters
  useEffect(() => {
    let filtered = [...agents];

    if (selectedSpecialization) {
      filtered = filtered.filter(
        (agent) => agent.specialization === selectedSpecialization
      );
    }

    if (selectedLanguage) {
      filtered = filtered.filter((agent) =>
        agent.languages?.includes(selectedLanguage)
      );
    }

    setFilteredAgents(filtered);
    setActiveIndex(0); // Reset to first agent when filters change
  }, [selectedSpecialization, selectedLanguage, agents]);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && filteredAgents.length > 1) {
      intervalRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % filteredAgents.length);
      }, 3000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoPlay, filteredAgents.length]);

  // Trigger animation when section comes into view
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // Navigation functions
  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % filteredAgents.length);
  };

  const prevSlide = () => {
    setActiveIndex(
      (prev) => (prev - 1 + filteredAgents.length) % filteredAgents.length
    );
  };

  // Pause autoplay on hover
  const handleMouseEnter = () => {
    setAutoPlay(false);
  };

  const handleMouseLeave = () => {
    setAutoPlay(true);
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedSpecialization('');
    setSelectedLanguage('');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest('.specialization-dropdown') &&
        !e.target.closest('.language-dropdown')
      ) {
        setIsSpecializationOpen(false);
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function useHasMounted() {
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
      setHasMounted(true);
    }, []);
    return hasMounted;
  }

  const hasMounted = useHasMounted();

  if (!hasMounted) return null; // Prevent SSR mismatch

  return (
    <motion.section
      ref={ref}
      className='relative py-16 px-4 bg-gray-50 dark:bg-gray-800 overflow-hidden'
      variants={sectionVariants}
      initial='hidden'
      animate={controls}
    >
      <div className='max-w-7xl mx-auto'>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center lg:mb-24'>
          {/* Left Column - Title and Description */}
          <motion.div className='space-y-6 lg:ml-24' variants={contentVariants}>
            <motion.div
              className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-primarycolor/10 mb-4'
              variants={titleVariants}
            >
              <Users className='w-8 h-8 text-primarycolor' />
            </motion.div>

            <motion.h2
              className='text-4xl font-bold text-gray-900 dark:text-white leading-tight'
              variants={titleVariants}
            >
              {t('meetOurAgents')} <br />
              <span className='text-primarycolor'>{t('realEstateAgents')}</span>
            </motion.h2>

            <motion.p
              className='text-gray-600 dark:text-gray-400 text-lg'
              variants={titleVariants}
            >
              {t('agentsExperience')}
            </motion.p>

            <motion.p
              className='text-gray-600 dark:text-gray-400 text-lg'
              variants={titleVariants}
            >
              {t('buySellInvest')}
            </motion.p>

            {/* Filter Dropdowns */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-8'>
              {/* Specialization Dropdown */}
              <div className='relative specialization-dropdown'>
                <button
                  className='w-full flex items-center justify-between px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primarycolor dark:hover:border-secondarycolor transition-colors bg-white dark:bg-gray-900'
                  onClick={() => {
                    setIsSpecializationOpen(!isSpecializationOpen);
                    if (isLanguageOpen) setIsLanguageOpen(false);
                  }}
                >
                  <span className='text-gray-700 dark:text-gray-300'>
                    {selectedSpecialization || t('specialization')}
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform text-gray-500 ${isSpecializationOpen ? 'rotate-180' : ''}`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M19 9l-7 7-7-7'
                    ></path>
                  </svg>
                </button>

                {isSpecializationOpen && (
                  <div className='absolute z-30 mt-1 w-full bg-white dark:bg-gray-900 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-40 overflow-y-auto'>
                    {specializations.map((specialization) => (
                      <button
                        key={specialization}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                          selectedSpecialization === specialization
                            ? 'bg-primarycolor dark:bg-secondarycolor text-white dark:text-black'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                        onClick={() => {
                          setSelectedSpecialization((prev) =>
                            prev === specialization ? '' : specialization
                          );
                          setIsSpecializationOpen(false);
                        }}
                      >
                        {specialization}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Language Dropdown */}
              <div className='relative language-dropdown'>
                <button
                  className='w-full flex items-center justify-between px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primarycolor dark:hover:border-secondarycolor transition-colors bg-white dark:bg-gray-900'
                  onClick={() => {
                    setIsLanguageOpen(!isLanguageOpen);
                    if (isSpecializationOpen) setIsSpecializationOpen(false);
                  }}
                >
                  <span className='text-gray-700 dark:text-gray-300'>
                    {selectedLanguage || t('languages')}
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform text-gray-500 ${isLanguageOpen ? 'rotate-180' : ''}`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M19 9l-7 7-7-7'
                    ></path>
                  </svg>
                </button>

                {isLanguageOpen && (
                  <div className='absolute z-30 mt-1 w-full bg-white dark:bg-gray-900 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-40 overflow-y-auto'>
                    {languages.map((language) => (
                      <button
                        key={language}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                          selectedLanguage === language
                            ? 'bg-primarycolor dark:bg-secondarycolor text-white dark:text-black'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                        onClick={() => {
                          setSelectedLanguage((prev) =>
                            prev === language ? '' : language
                          );
                          setIsLanguageOpen(false);
                        }}
                      >
                        {language}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <motion.div variants={titleVariants}>
              <motion.button
                onClick={() => router.push('/agents')}
                className='w-full md:max-w-xs mt-4 px-4 py-3 bg-primarycolor text-white hover:bg-secondarycolor hover:text-black transition-colors rounded-md flex items-center justify-center mx-auto'
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('showAllAgents')}
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Right Column - 3D Carousel Slider */}
          <div className='relative w-full'>
            {/* Slider Controls */}
            <div className='absolute top-1/2 left-0 right-0 flex justify-between px-4 md:px-0 -translate-y-1/2 z-20'>
              <motion.button
                onClick={() => {
                  prevSlide();
                  setAutoPlay(false);
                  setTimeout(() => setAutoPlay(true), 5000);
                }}
                className='p-3 rounded-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primarycolor dark:hover:bg-secondarycolor hover:text-white dark:hover:text-black hover:border-primarycolor dark:hover:border-secondarycolor transition-colors shadow-lg'
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label='Previous slide'
              >
                <ChevronLeft className='w-6 h-6' />
              </motion.button>

              <motion.button
                onClick={() => {
                  nextSlide();
                  setAutoPlay(false);
                  setTimeout(() => setAutoPlay(true), 5000);
                }}
                className='p-3 rounded-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primarycolor dark:hover:bg-secondarycolor hover:text-white dark:hover:text-black hover:border-primarycolor dark:hover:border-secondarycolor transition-colors shadow-lg'
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label='Next slide'
              >
                <ChevronRight className='w-6 h-6' />
              </motion.button>
            </div>

            {/* 3D Carousel */}
            <div
              className='relative h-[550px] w-full overflow-visible flex justify-center items-center'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {isLoading ? (
                <div className='text-center p-8 bg-white dark:bg-gray-900 rounded-xl shadow-lg'>
                  <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primarycolor mx-auto mb-4'></div>
                  <p className='text-gray-600 dark:text-gray-400'>
                    Loading agents...
                  </p>
                </div>
              ) : filteredAgents.length > 0 ? (
                filteredAgents.map((agent, index) => {
                  const position =
                    (index - activeIndex + filteredAgents.length) %
                    filteredAgents.length;

                  const angle =
                    (position / filteredAgents.length) * 1 * Math.PI;
                  const radius = 400;

                  const x = Math.sin(angle) * radius;
                  const z = Math.cos(angle) * radius - radius;

                  let scale = 0.8;
                  let opacity = 0.6;
                  let zIndex = 0;

                  if (position === 0) {
                    scale = 1;
                    opacity = 1;
                    zIndex = 10;
                  } else if (
                    position === 1 ||
                    position === filteredAgents.length - 1
                  ) {
                    scale = 0.9;
                    opacity = 0.8;
                    zIndex = 5;
                  }

                  return (
                    <motion.div
                      key={agent._id}
                      className='absolute w-full max-w-[350px]'
                      initial={false}
                      animate={{
                        x,
                        z,
                        scale,
                        opacity,
                        rotateY: -angle * (180 / Math.PI),
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30,
                        mass: 1,
                      }}
                      style={{
                        zIndex: position === 0 ? 10 : 0,
                        transformStyle: 'preserve-3d',
                      }}
                      onClick={() => {
                        if (position !== 0) {
                          setActiveIndex(index);
                          setAutoPlay(false);
                          setTimeout(() => setAutoPlay(true), 5000);
                        }
                      }}
                    >
                      <Agent3DCard agent={agent} index={position} />
                    </motion.div>
                  );
                })
              ) : (
                <div className='text-center p-8 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700'>
                  <p className='text-gray-600 dark:text-gray-400 mb-4'>
                    No agents match the selected filters.
                  </p>
                  <motion.button
                    className='px-4 py-2 bg-primarycolor text-white hover:bg-secondarycolor hover:text-black transition-colors rounded-md flex items-center justify-center mx-auto gap-2'
                    onClick={resetFilters}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <RefreshCw className='w-4 h-4' />
                    Reset Filters
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
